import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { classificationEndpoints } from '@/lib/api/classification';
import type { DatasetClassificationResult } from '../types/classification.types';

/** Centralized query keys for classification data. */
export const classificationKeys = {
  all: ['classification'] as const,
  details: () => [...classificationKeys.all, 'detail'] as const,
  detail: (datasetId: string) => [...classificationKeys.details(), datasetId] as const,
};

/** Read an HTTP status off whatever the api client threw. */
function statusOf(error: unknown): number | undefined {
  const e = error as { status?: number; response?: { status?: number } };
  return e?.status ?? e?.response?.status;
}

/**
 * Fetch a dataset's latest classification (#1481).
 *
 * A 404 means "never classified" — it resolves to `null` so the tab renders
 * its empty state instead of an error card.
 */
export function useDatasetClassification(datasetId: string | undefined) {
  return useQuery<DatasetClassificationResult | null>({
    queryKey: classificationKeys.detail(datasetId ?? ''),
    enabled: Boolean(datasetId),
    queryFn: async () => {
      try {
        const response = await classificationEndpoints.getLatest(datasetId as string);
        return response.data;
      } catch (error) {
        if (statusOf(error) === 404) return null;
        throw error;
      }
    },
  });
}

/** Trigger a re-classification and refresh the cached result on success. */
export function useReclassifyDataset(datasetId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => classificationEndpoints.trigger(datasetId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: classificationKeys.detail(datasetId ?? ''),
      });
    },
  });
}
